import {
  Box,
  chakra,
  Flex,
  Grid,
  GridItem,
  Link,
  Spacer,
  Tooltip,
  useStyleConfig,
  VStack,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { type MutableRefObject, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { NavLink } from 'react-router-dom';

import {
  filterNavItemsForUser,
  type NavItemConfig,
  type RouteNavConfig,
} from '@revelio/auth';
import { TourClasses, useTourStore } from '@revelio/core';

import { Logo } from './Logo';
import { AccountMenu } from './menus/account-menu';
import { MethodologyMenu } from './menus/methodology-menu';
import { NavIconChild, navIconBg } from './nav-icon';
import { SubNav } from './sub-nav/sub-nav';
import { useEnforceUserIntroduction } from './useEnforceUserIntroduction';

export interface WithSpotlightWrapperProps {
  condition: boolean;
  children: JSX.Element;
}

const WithSpotlightWrapper = ({
  condition,
  children,
}: WithSpotlightWrapperProps) => {
  return condition ? (
    <Flex
      className={TourClasses.TOUR_CONTROL_PANEL_CLASS}
      direction="column"
      alignItems="center"
      justifyContent="flex-start"
      w="100%"
      rowGap="5px"
    >
      {children}
    </Flex>
  ) : (
    children
  );
};
export interface SideNavProps {
  items: (NavItemConfig | RouteNavConfig)[];
  version?: string;
  onOpenImpersonationModal?: () => void;
}

export function SideNav(props: SideNavProps) {
  const linkStyles = useStyleConfig('Link', { variant: 'nav' });

  const [{ pagesBeforeDivider, pagesAfterDivider }, setPages] = useState<
    Record<string, RouteNavConfig[]>
  >({
    pagesBeforeDivider: [],
    pagesAfterDivider: [],
  });

  const { loggedInUser, fetching } = useEnforceUserIntroduction();

  const isTour = useTourStore((s) => s.isTour);

  useEffect(() => {
    if (fetching || isEmpty(loggedInUser)) {
      return;
    }

    const userTabs = loggedInUser.tabs || [];
    const userRole = loggedInUser.role;

    const pages = filterNavItemsForUser(props.items, userTabs, userRole).reduce(
      (acc, page) => {
        if (page.excludeFromSideNavButtons) return acc;

        return [...acc, page];
      },
      [] as RouteNavConfig[]
    );

    const pagesBeforeDivider = pages.filter((page) => page.isSourcesTab);
    const pagesAfterDivider = pages.filter((page) => !page.isSourcesTab);

    setPages({ pagesBeforeDivider, pagesAfterDivider });
  }, [fetching, loggedInUser, props.items]);

  const sideNavRef = useRef<HTMLDivElement>(null);

  const renderPageTabs = (pages: RouteNavConfig[]) => {
    return pages.map((item) =>
      /* eslint-disable-next-line no-nested-ternary */
      !item.children ? (
        /* eslint-disable-next-line no-nested-ternary */
        // biome-ignore lint/style/noNestedTernary: migrated from develop
        item.element ? (
          <Link
            key={item.name}
            className="nav"
            as={NavLink}
            to={item.path || ''}
            bgImg={navIconBg(item.icon)}
            variant="nav"
            position="relative"
          >
            <NavIconChild icon={item.icon} />
          </Link>
          // biome-ignore lint/style/noNestedTernary: migrated from develop
        ) : item.elementId && !item.path ? (
          // this is when the button is controlled by something external (e.g. jira script in html script)
          <Tooltip hasArrow placement="right" key={item.name} label={item.name}>
            <chakra.a
              key={item.name}
              sx={linkStyles}
              className="nav"
              id={item.elementId}
              bgImg={navIconBg(item.icon)}
              position="relative"
            >
              <NavIconChild icon={item.icon} />
            </chakra.a>
          </Tooltip>
        ) : (
          <chakra.a
            key={item.name}
            sx={linkStyles}
            className="nav"
            href={item.path}
            bgImg={navIconBg(item.icon)}
            position="relative"
          >
            <NavIconChild icon={item.icon} />
          </chakra.a>
        )
      ) : (
        <SubNav
          key={item.name}
          parent={item}
          items={item.children as RouteNavConfig[]}
          sideNavRef={sideNavRef as MutableRefObject<Element>}
        />
      )
    );
  };

  const navigate = useNavigate();

  return (
    <Grid templateRows="1" templateColumns="54px 1fr">
      <GridItem
        w="54px"
        className="rl-sidenav"
        ref={sideNavRef}
        position="relative"
      >
        <VStack
          align="center"
          height="100vh"
          backgroundColor="sidebar.500"
          padding="14px 8px"
          spacing={5}
        >
          <Box
            data-testid="sidebar-logo"
            height="60px"
            width="44px"
            paddingEnd="14px"
            transform="scale(0.9)"
            onClick={() => navigate('/')}
            cursor="pointer"
            transition="opacity 0.1s ease-in-out"
            _hover={{ opacity: 0.7 }}
          >
            <Logo />
          </Box>

          <WithSpotlightWrapper condition={isTour}>
            {/* @ts-expect-error noUncheckedIndexedAccess */}
            <>{renderPageTabs(pagesBeforeDivider)}</>
          </WithSpotlightWrapper>

          {/* @ts-expect-error noUncheckedIndexedAccess */}
          {renderPageTabs(pagesAfterDivider)}
          <Spacer />
          <MethodologyMenu />
          <AccountMenu
            version={`v${props.version}`}
            onOpenImpersonationModal={props.onOpenImpersonationModal}
          />
        </VStack>
      </GridItem>
      <GridItem>
        <Outlet></Outlet>
      </GridItem>
    </Grid>
  );
}

export default SideNav;
